import './styles/ArticlesOverview.scss';

import { Check, Close, Delete, Edit } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { Article } from '@thro-types/leihbar';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import { useLeihbarArticleMutation, useLeihbarArticlesForProductQuery } from 'api/hooks/leihbar';
import { CreateButton, DataGrid, ConfirmationDialog, TypedGridColumns } from 'app/components';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';

import { usePermissionToCreate } from '../../helper';

export const ArticlesOverview = () => {
  const { t } = useTranslation();
  const ableToCreate = usePermissionToCreate();
  const { id: productId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentURL = useCurrentURL();

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });
  const [articleRef, setArticleRef] = useState<Article | undefined>();
  const [deleteArticleDialogOpen, setDeleteArticleDialogOpen] = useState(false);

  const { data: articles, isLoading } = useLeihbarArticlesForProductQuery(parseInt(productId!), {
    params: {
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize
    }
  });
  const { deletion } = useLeihbarArticleMutation();

  const handlePaginationChange = useCallback(pagination => {
    setPaginationModel(pagination);
  }, []);

  const handleDelete = async () => {
    try {
      if (!articleRef || !productId) {
        return;
      }
      await deletion.mutateAsync({
        id: articleRef.id,
        productId: parseInt(productId)
      });
      dispatch(
        AlertActions.success(t(translations.pages.leihbar.article.alerts.successfulDelete()))
      );
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.leihbar.article.alerts.failedDelete())));
    }
  };

  const rowActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={<Edit sx={{ color: 'lightblue' }} />}
          label={t(translations.common.edit())}
          onClick={() =>
            navigate(`/leihbar/article/edit/${params.row.id}`, {
              state: {
                back: currentURL,
                productId: productId,
                article: params.row
              }
            })
          }
          showInMenu
        />,
        <GridActionsCellItem
          icon={<Delete sx={{ color: 'red' }} />}
          label={t(translations.common.delete())}
          onClick={() => {
            setArticleRef(params.row);
            setDeleteArticleDialogOpen(true);
          }}
          showInMenu
        />
      ];
    },
    [navigate, productId, t, currentURL]
  );

  const columns = useMemo((): TypedGridColumns<Article> => {
    return [
      {
        field: 'sku',
        flex: 1,
        headerName: t(translations.pages.leihbar.article.columns.articleId())
      },
      {
        field: 'isVisible',
        flex: 3,
        headerName: t(translations.pages.leihbar.article.columns.visibility()),
        renderCell: params => {
          return params.row.isVisible ? <Check /> : <Close />;
        }
      },
      {
        field: 'internalNote',
        flex: 3,
        headerName: t(translations.pages.leihbar.internalArticleNote())
      },
      {
        flex: 1,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [rowActions, t]);

  return (
    <>
      <ConfirmationDialog
        open={deleteArticleDialogOpen}
        title={t(translations.pages.leihbar.article.dialogs.deleteConfirm.title())}
        onClose={() => {
          setDeleteArticleDialogOpen(false);
        }}
        onConfirm={async () => {
          await handleDelete();
          setDeleteArticleDialogOpen(false);
        }}
        isPending={deletion.isPending}
      >
        <div>{t(translations.pages.leihbar.article.dialogs.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <Grid container>
        {ableToCreate && (
          <Grid
            container
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}
          >
            <CreateButton
              onClick={() =>
                navigate('/leihbar/article/create', {
                  state: {
                    back: currentURL,
                    productId: productId
                  }
                })
              }
              sx={{ margin: '1em' }}
            />
          </Grid>
        )}
        <Grid container style={{ height: '70vh' }}>
          <DataGrid
            columns={columns}
            rows={articles?.data || []}
            style={{ height: '100%', width: '100%' }}
            paginationModel={{
              page: paginationModel.page,
              pageSize: paginationModel.pageSize
            }}
            onPaginationModelChange={pagModel => {
              handlePaginationChange(pagModel);
            }}
            getRowHeight={() => 'auto'}
            loading={isLoading}
          />
        </Grid>
      </Grid>
    </>
  );
};
